import { useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "@/api/email";
import { restorePilotReviewMessage, type ReviewMessage } from "@/api/pilotEditorial";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { reviewTextDiff } from "@/lib/pilotReview";
import { cn } from "@/lib/utils";

const origins: Record<string, string> = {
  individual: "Modification individuelle", template: "Modification du modèle", restoration: "Restauration",
  automatic: "Traitement automatique", snapshot: "État conservé avant modification", current: "État actuel",
};
function date(value: string | null) {
  if (!value) return "Date non enregistrée";
  return new Intl.DateTimeFormat("fr-FR", {dateStyle: "short", timeStyle: "medium"}).format(new Date(value));
}
function Difference({ before, after }: {before: string; after: string}) {
  return <div className="whitespace-pre-wrap break-words text-sm leading-6">{reviewTextDiff(before, after).map((part, index) =>
    part.kind === "added" ? <ins key={index} className="bg-emerald-100 text-emerald-900 no-underline">{part.text}</ins> :
      part.kind === "removed" ? <del key={index} className="bg-rose-100 text-rose-900">{part.text}</del> : <span key={index}>{part.text}</span>
  )}</div>;
}

export function PilotMessageHistory({message, slug, campaignId, name, variantLabel, canEdit, onClose, onRestored}: {
  message: ReviewMessage; slug: string; campaignId: number; name: string; variantLabel?: string; canEdit: boolean;
  onClose: () => void; onRestored: () => Promise<void>;
}) {
  const [selected, setSelected] = useState(message.history.find((v) => !v.current)?.id ?? message.history[0]?.id);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const version = message.history.find((v) => v.id === selected);
  const differs = version && (version.subject !== message.subject || version.body !== message.body);
  async function restore() {
    if (!version || busy) return;
    setBusy(true); setError("");
    try {
      await restorePilotReviewMessage(slug, campaignId, message, version.id);
      await onRestored();
      toast.success("Version restaurée pour cet email uniquement");
      onClose();
    } catch (cause) {setError(errorMessage(cause));}
    finally {setBusy(false); setConfirm(false);}
  }
  return <>
    <Sheet open onOpenChange={(open) => {if (!open && !busy) onClose();}}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-[720px]" onInteractOutside={(e) => {if (busy) e.preventDefault();}} onEscapeKeyDown={(e) => {if (busy) e.preventDefault();}}>
        <SheetHeader className="shrink-0 pb-4 pr-6">
          <SheetTitle>Historique de cet email</SheetTitle>
          <SheetDescription>{name} · {message.position === 1 ? `J0 · ${variantLabel ?? (message.variant === "B" ? "B Neurosud" : "A Courte")}` : ["", "", "J+4", "J+11", "J+21"][message.position]}</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div role="list" aria-label="Versions disponibles" className="space-y-2">
            {message.history.map((v) => <button key={v.id} role="listitem" disabled={busy} aria-current={selected === v.id ? "true" : undefined}
              onClick={() => setSelected(v.id)} className={cn("block w-full rounded-lg border p-3 text-left text-sm", selected === v.id ? "border-slate-800 bg-slate-50" : "border-slate-200")}>
              <span className="block font-medium">{v.description ?? origins[v.origin] ?? v.origin}{v.current ? " · Actuelle" : ""}</span>
              <span className="mt-1 block text-xs text-slate-600">{date(v.at)} · {v.author ?? "Auteur non enregistré"}{v.actor_type === "system" ? " · automatique" : ""}</span>
            </button>)}
          </div>
          {message.unavailable_history && <p className="mt-3 text-xs leading-5 text-slate-500">Les anciennes versions sans texte personnalisé enregistré ne sont pas reconstituées. Seules les copies effectivement conservées sont proposées.</p>}
          {version && <section aria-label="Comparaison avec la version actuelle" className="mt-6 space-y-4 border-t border-slate-200 pt-5">
            <div><h3 className="text-sm font-semibold">Version sélectionnée → version actuelle</h3>
              <p className="mt-1 text-xs text-slate-600"><span className="bg-rose-100 text-rose-900">Suppressions barrées</span> · <span className="bg-emerald-100 text-emerald-900">Ajouts en vert</span></p></div>
            <div><h4 className="mb-2 text-xs font-semibold uppercase text-slate-500">Objet</h4><Difference before={version.subject} after={message.subject}/></div>
            <div><h4 className="mb-2 text-xs font-semibold uppercase text-slate-500">Corps</h4><Difference before={version.body} after={message.body}/></div>
            {!differs && <p className="text-sm text-slate-500">Texte identique à la version actuelle.</p>}
          </section>}
        </div>
        {error && <p role="alert" className="py-2 text-sm text-rose-800">{error}</p>}
        {canEdit && <footer className="shrink-0 border-t border-slate-100 pt-4"><Button disabled={!differs || busy} onClick={() => setConfirm(true)}>Restaurer cette version</Button></footer>}
      </SheetContent>
    </Sheet>
    <Dialog open={confirm} onOpenChange={(open) => {if (!busy) setConfirm(open);}}>
      <DialogContent onInteractOutside={(e) => {if (busy) e.preventDefault();}} onEscapeKeyDown={(e) => {if (busy) e.preventDefault();}}>
        <DialogHeader><DialogTitle>Restaurer cet email ?</DialogTitle><DialogDescription>Seul ce message de {name}{message.position === 1 ? `, variante ${message.variant}` : ""} sera restauré. Une nouvelle version conservera cette action ; les modifications intermédiaires, le modèle et les autres prospects restent préservés.</DialogDescription></DialogHeader>
        <DialogFooter><Button variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>Annuler</Button><Button disabled={busy} onClick={() => void restore()}>{busy ? "Restauration…" : "Confirmer la restauration"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
